// Features: M004-F0796 (apportionment editor), M004-F0797 ("Test this rule" preview). WebUI #263, slice B2.
// Tests: T-M004-0416 (apportionment validation, RTL), T-M004-0420 (Edyta preview, jest half), T-M004-0421 (the reply is rendered, not recomputed)
// The UI does not route bookings. The preview's routing is the reply's; labels are looked up by key only.
import React, { useState } from 'react';
import { customerBillingProfileApi } from '../../../services/customerBillingProfileApi';
import { APPORTIONMENT_HEADING, refusalSentence } from './billingProfileModel';
import { slugify } from './GroupsEditor';

export const MAX_RULES = 8;
const MIN_PARTS = 2;
const MAX_PARTS = 6;
export const TEST_SENTENCE = 'Nothing is saved. This is a test of the rule you have entered.';
const WEEKDAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];
const MONEY_TEXT = /^\d+(\.\d{1,2})?$/;

let uidSeq = 0;
const newUid = () => `rule-${++uidSeq}`;

// Pounds typed by the operator to whole pence. Text the model cannot accept becomes NaN, which the model refuses (the mirror, not the judge).
export function poundsToMinor(text) {
  const s = String(text ?? '').trim();
  if (s === '') return undefined;
  if (!MONEY_TEXT.test(s)) return NaN;
  return Math.round(Number(s) * 100);
}

const wholeOrUndefined = (text) => {
  const s = String(text ?? '').trim();
  return s === '' ? undefined : Number(s);
};

const blankPart = () => ({ uid: newUid(), group_key: '', fixed: '', remainder: false, weight: '' });

export function blankRule() {
  return {
    uid: newUid(), saved: false, rule_id: '', label: '', mode: 'waterfall', descriptionContains: '', weekday: [], startFrom: '',
    startTo: '', roomId: '', otherMatch: {}, parts: [blankPart(), blankPart()],
  };
}

// A saved rule as editable rows. Match keys this editor does not show (nature, dates, booking) ride along unchanged.
export function rowsFromRules(rules) {
  return (Array.isArray(rules) ? rules : []).map((r) => {
    const { description_contains: descriptionContains, weekday, start_time_from: startFrom, start_time_to: startTo, room_id: roomId,
      ...otherMatch } = r?.match || {};
    return {
      uid: newUid(), saved: true, rule_id: r?.rule_id ?? '', label: r?.label ?? '', mode: r?.mode === 'ratio' ? 'ratio' : 'waterfall',
      descriptionContains: descriptionContains ?? '', weekday: Array.isArray(weekday) ? weekday : (weekday ? [weekday] : []),
      startFrom: startFrom ?? '', startTo: startTo ?? '', roomId: roomId ?? '', otherMatch,
      parts: (r?.parts || []).map((p) => ({
        uid: newUid(),
        group_key: p.group_key ?? '',
        fixed: p.fixed_minor != null ? (p.fixed_minor / 100).toFixed(2) : '',
        remainder: p.remainder === true,
        weight: p.weight != null ? String(p.weight) : '',
      })),
    };
  });
}

// The contract's apportionments[] from the rows. A new rule's rule_id is its label's slug; a saved rule keeps its id.
export function rulesForBody(rows) {
  return rows.map((r) => {
    const match = { ...r.otherMatch };
    if (r.descriptionContains !== '') match.description_contains = r.descriptionContains;
    const days = WEEKDAYS.map(([value]) => value).filter((value) => r.weekday.includes(value));
    if (days.length) match.weekday = days;
    if (r.startFrom !== '') match.start_time_from = r.startFrom;
    if (r.startTo !== '') match.start_time_to = r.startTo;
    if (r.roomId !== '') match.room_id = r.roomId;
    const parts = r.parts.map((p) => {
      if (r.mode === 'ratio') return { group_key: p.group_key, weight: wholeOrUndefined(p.weight) };
      if (p.remainder) return { group_key: p.group_key, remainder: true };
      return { group_key: p.group_key, fixed_minor: poundsToMinor(p.fixed) };
    });
    return { rule_id: r.rule_id || slugify(r.label), label: r.label, mode: r.mode, match, parts };
  });
}

const money = (minor) => `£${(Number(minor) / 100).toFixed(2)}`;

// "Via" in words. The reply's via is shown; the rule and group are only named, never chosen here.
export function viaText(via, rules, groups) {
  if (via === 'default') return 'Default group';
  if (via === 'ambiguous') return 'Ambiguous: more than one group matches';
  if (String(via).startsWith('apportionment:')) {
    const id = via.slice('apportionment:'.length);
    return `Bill part of matching bookings to another group (rule ${rules.find((r) => r.rule_id === id)?.label || id})`;
  }
  if (String(via).startsWith('group:')) {
    const key = via.slice('group:'.length);
    return `Matched group ${groups.find((g) => g.group_key === key)?.label || key}`;
  }
  return String(via ?? '');
}

const Err = ({ text }) => (text ? <p role="alert" className="bps-field-error">{text}</p> : null);

function PreviewResult({ reply, rules, groups }) {
  const lines = Array.isArray(reply.lines) ? reply.lines : [];
  const ambiguous = lines.filter((l) => l.via === 'ambiguous').length;
  const unrouted = Number.isInteger(reply.unrouted) ? reply.unrouted : 0;
  return (
    <section data-testid="apptn-preview" aria-label="Test result" className="bps-preview">
      <p role="status">{TEST_SENTENCE}</p>
      <p data-testid="apptn-ambiguous-count">Ambiguous: {ambiguous}</p>
      <p data-testid="apptn-unrouted-count">Not routed: {unrouted}</p>
      {lines.length === 0 ? <p>No bookings or open draft lines to test.</p> : null}
      <ul>
        {lines.map((line, i) => (
          <li key={line.hand_off_id ?? line.line_id ?? i} data-testid={`apptn-line-${i}`}
            className={line.via === 'ambiguous' ? 'bps-ambiguous' : undefined}>
            <span>{line.date}</span> <span>{line.description}</span> <span>{money(line.price_minor)}</span>
            <ul>
              {(line.routed_to || []).map((r, j) => (
                <li key={`${r.group_key}-${j}`}>
                  {groups.find((g) => g.group_key === r.group_key)?.label || r.group_key}: {money(r.amount_minor)}
                </li>
              ))}
            </ul>
            <p data-testid={`apptn-via-${i}`}>{viaText(line.via, rules, groups)}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

// rows: the editor's rules. groups: the candidate groups as the request body has them (group_key, label).
// errors: [{ path, message }] from validateProfileDraft. serverErrors: { path: sentence } from a refusal.
export default function ApportionmentEditor({ orgId, userId, rows, groups, onChange, errors = [], serverErrors = {} }) {
  const [test, setTest] = useState({ status: 'idle', reply: null, message: '' });
  const update = (uid, patch) => onChange((prev) => prev.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));
  const updatePart = (uid, partUid, patch) => onChange((prev) => prev.map((r) => (r.uid !== uid ? r : {
    ...r, parts: r.parts.map((p) => (p.uid === partUid ? { ...p, ...patch } : p)),
  })));
  const rulesErrors = (base) => errors.filter((e) => e.path === base || e.path.startsWith(`${base}.`) || e.path.startsWith(`${base}[`));
  const serverFor = (base) => Object.entries(serverErrors).filter(([path]) => path === base || path.startsWith(`${base}.`)).map(([, m]) => m);
  const outside = errors.filter((e) => e.path === 'apportionments');
  const atLimit = rows.length >= MAX_RULES;
  const errorsForTest = errors.length > 0;

  const runTest = async () => {
    setTest({ status: 'checking', reply: null, message: '' });
    try {
      // The candidate, as it stands unsaved. Nothing is written: this is a SAL 3 read.
      const res = await customerBillingProfileApi.preview(orgId, userId, { groups, apportionments: rulesForBody(rows) });
      setTest({ status: 'done', reply: res?.data?.data || {}, message: '' });
    } catch (cause) {
      setTest({ status: 'error', reply: null, message: refusalSentence(cause) });
    }
  };

  return (
    <section className="bps-section" aria-labelledby="bp-apptn-heading" data-testid="billing-apportionments-editor">
      <h4 id="bp-apptn-heading">Apportionments</h4>
      {rows.map((rule, index) => {
        const base = `apportionments[${index}]`;
        const ruleMessages = [...rulesErrors(base).map((e) => e.message), ...serverFor(base)];
        const partsWord = rule.parts.length;
        return (
          <fieldset key={rule.uid} className="bps-group" data-testid={`bp-apptn-${index}`}>
            <legend>{APPORTIONMENT_HEADING}</legend>
            <label>Rule label
              <input type="text" value={rule.label} maxLength={80} onChange={(e) => update(rule.uid, { label: e.target.value })} />
            </label>
            <fieldset>
              <legend>Matching bookings</legend>
              <label>Description contains
                <input type="text" value={rule.descriptionContains} maxLength={80}
                  onChange={(e) => update(rule.uid, { descriptionContains: e.target.value })} />
              </label>
              <div role="group" aria-label="Weekdays">
                {WEEKDAYS.map(([value, text]) => (
                  <label key={value}>
                    <input type="checkbox" checked={rule.weekday.includes(value)} onChange={() => update(rule.uid, {
                      weekday: rule.weekday.includes(value) ? rule.weekday.filter((d) => d !== value) : [...rule.weekday, value],
                    })} />
                    {text}
                  </label>
                ))}
              </div>
              <label>Start time from
                <input type="time" value={rule.startFrom} onChange={(e) => update(rule.uid, { startFrom: e.target.value })} />
              </label>
              <label>Start time before
                <input type="time" value={rule.startTo} onChange={(e) => update(rule.uid, { startTo: e.target.value })} />
              </label>
              <label>Room ID
                <input type="text" value={rule.roomId} maxLength={64} onChange={(e) => update(rule.uid, { roomId: e.target.value })} />
              </label>
            </fieldset>
            <fieldset>
              <legend>How to share</legend>
              <label><input type="radio" name={`${rule.uid}-mode`} checked={rule.mode === 'waterfall'}
                onChange={() => update(rule.uid, { mode: 'waterfall' })} /> Waterfall (fixed amounts, then the rest)</label>
              <label><input type="radio" name={`${rule.uid}-mode`} checked={rule.mode === 'ratio'}
                onChange={() => update(rule.uid, { mode: 'ratio' })} /> Ratio (shares)</label>
            </fieldset>
            {rule.parts.map((part, j) => (
              <fieldset key={part.uid} data-testid={`bp-apptn-${index}-part-${j}`}>
                <legend>Part {j + 1}</legend>
                <label>Group for part {j + 1}
                  <select value={part.group_key} onChange={(e) => updatePart(rule.uid, part.uid, { group_key: e.target.value })}>
                    <option value="">Choose a group</option>
                    {groups.map((g) => <option key={g.group_key} value={g.group_key}>{g.label}</option>)}
                  </select>
                </label>
                {rule.mode === 'waterfall' ? (
                  <>
                    {part.remainder ? null : (
                      <label>Fixed amount (£)
                        <input type="text" inputMode="decimal" value={part.fixed}
                          onChange={(e) => updatePart(rule.uid, part.uid, { fixed: e.target.value })} />
                      </label>
                    )}
                    <label>
                      <input type="radio" name={`${rule.uid}-remainder`} checked={part.remainder}
                        onChange={() => onChange((prev) => prev.map((r) => (r.uid !== rule.uid ? r : {
                          ...r, parts: r.parts.map((p) => ({ ...p, remainder: p.uid === part.uid, fixed: p.uid === part.uid ? '' : p.fixed })),
                        })))} />
                      Remainder
                    </label>
                  </>
                ) : (
                  <label>Share (whole number)
                    <input type="number" step="1" value={part.weight} onChange={(e) => updatePart(rule.uid, part.uid, { weight: e.target.value })} />
                  </label>
                )}
              </fieldset>
            ))}
            <div>
              <button type="button" className="bps-secondary" disabled={partsWord >= MAX_PARTS}
                onClick={() => update(rule.uid, { parts: [...rule.parts, blankPart()] })}>Add part</button>
              <button type="button" className="bps-secondary" disabled={partsWord <= MIN_PARTS}
                onClick={() => update(rule.uid, { parts: rule.parts.slice(0, -1) })}>Remove part</button>
              <button type="button" className="bps-secondary"
                onClick={() => onChange((prev) => prev.filter((r) => r.uid !== rule.uid))}>Remove rule</button>
            </div>
            {ruleMessages.map((text) => <Err key={text} text={text} />)}
          </fieldset>
        );
      })}
      <Err text={outside.map((e) => e.message).join(' ')} />
      {atLimit ? <p>A profile has at most {MAX_RULES} apportionment rules. Remove one to add another.</p> : null}
      <button type="button" className="bps-secondary" disabled={atLimit}
        onClick={() => onChange((prev) => [...prev, blankRule()])}>Add rule</button>
      <div>
        <button type="button" className="bps-secondary" data-testid="apptn-test" disabled={errorsForTest || test.status === 'checking'}
          onClick={runTest}>Test this rule</button>
      </div>
      <Err text={test.message} />
      {test.status === 'done' ? <PreviewResult reply={test.reply} rules={rulesForBody(rows)} groups={groups} /> : null}
    </section>
  );
}
